import React, { useEffect, useState } from "react";
import {
  X,
  FileText,
  Loader2,
  Power,
  PowerOff,
  User as UserIcon,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Shield,
  Key,
  Globe,
  History,
  Edit,
} from "lucide-react";
import { RejectKYCModal } from "../../common/modals/RejectKYCModal";
import { ChangeCountryModal } from "./ChangeCountryModal";
import useCountries from "../../../hooks/countries/useCountries";
import CountryHistory from "../../users/sections/CountryHistory";
import { useNavigate } from "react-router-dom";
import { UpdateUserModal } from "./UpdateUserModal";
import { usersService } from "../../../services/usersService";

import { alertError, alertSuccess } from "../../../helpers/alertError";

// Types
export interface User {
  id: string;
  name: string;
  first_name: string;
  last_name: string;
  interact_email: string;
  email: string;
  country: string;
  countryFlag: string;
  kycStatus: "pending" | "approved" | "rejected" | "incomplete" | "not_requested";
  riskLevel: "low" | "medium" | "high";
  registrationDate: string;
  avatar?: string;
  documents: {
    id: boolean;
    passport: boolean;
    address: boolean;
    selfie: boolean;
  };
  lastActivity: string;
  status?: "ACTIVE" | "INACTIVE";
  phonenumber?: string;
  is_verified?: "YES" | "NO" | "PENDING" | "REJECTED" | boolean | number;
  connexion_type?: "EMAIL" | "GOOGLE" | "FACEBOOK" | "PHONE";
  role?: "USER" | "ADMIN" | "MODERATOR";
  country_id?: number;
  created_at?: string;
  updated_at?: string;
  password?: string;
  is_deleted?: boolean;
  fcm_token?: string | null;
  kyc_status?: "ACTIVE" | "INACTIVE" | "NO_KYC";
  has_kyc?: boolean;
  birthdate?: string;
  expired_document_date?: string;
  transaction_stats?: {
    total_sent: number;
    total_sent_cad: number;
    total_sent_currency: string;
    transaction_count: number;
  };
}

interface KYCDocument {
  id: string;
  user_id: string;
  document_type: string;
  passport_image: string | null;
  cni_permit_front_image: string | null;
  cni_permit_back_image: string | null;
  selfie_image: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface CountryChange {
  id: string;
  user_id: string;
  old_country_id: number;
  new_country_id: number;
  old_country_name?: string;
  new_country_name?: string;
  changed_by: string;
  created_at: string;
}

interface UserDetailsProps {
  user: User | null;
  onClose: () => void;
  onApprove: (user: User) => void;
  onReject: (user: User) => void;
  onUserUpdate?: (updatedUser?: User) => void;
}

// Utility Components
const UserAvatar: React.FC<{ name: string; avatar?: string }> = ({ name, avatar }) => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  if (avatar) {
    return <img src={avatar} alt={name} className="object-cover w-10 h-10 rounded-full" />;
  }

  return (
    <div className="flex justify-center items-center w-10 h-10 bg-green-100 rounded-full">
      <span className="text-sm font-medium text-green-700">{initials}</span>
    </div>
  );
};

const StatusBadge: React.FC<{ status: User["kycStatus"] }> = ({ status }) => {
  const statusConfig = {
    pending: { bg: "bg-yellow-100", text: "text-yellow-700", label: "En attente" },
    approved: { bg: "bg-green-100", text: "text-green-700", label: "Approuvé" },
    rejected: { bg: "bg-red-100", text: "text-red-700", label: "Rejeté" },
    incomplete: { bg: "bg-gray-100", text: "text-gray-700", label: "Incomplet" },
    not_requested: { bg: "bg-gray-100", text: "text-gray-700", label: "Non demandé" },
  };

  const config = statusConfig[status];

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>{config.label}</span>
  );
};

const getIsVerifiedBadge = (
  verified?: "YES" | "NO" | "PENDING" | "REJECTED" | boolean | number
): { label: string; bg: string; text: string } => {
  const raw =
    typeof verified === "string" ? verified.toUpperCase() : verified === true || verified === 1 ? "YES" : "NO";

  if (raw === "YES") {
    return { label: "Approuvé (YES)", bg: "bg-green-100", text: "text-green-700" };
  }
  if (raw === "PENDING") {
    return { label: "En attente (PENDING)", bg: "bg-yellow-100", text: "text-yellow-700" };
  }
  if (raw === "REJECTED") {
    return { label: "Rejeté (REJECTED)", bg: "bg-red-100", text: "text-red-700" };
  }
  return { label: "Non vérifié (NO)", bg: "bg-orange-100", text: "text-orange-700" };
};

// UserDetails Component (Modal)
export const UserDetails: React.FC<UserDetailsProps> = ({ user, onClose, onApprove, onReject, onUserUpdate }) => {
  const [kycData, setKycData] = useState<KYCDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  // NOUVEAUX ÉTATS
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showUpdateUserModal, setShowUpdateUserModal] = useState(false);
  const [countryHistory, setCountryHistory] = useState<CountryChange[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const navigate = useNavigate();
  const { countries } = useCountries({});

  useEffect(() => {
    if (user) {
      fetchKYCData();
      // NOUVEAU : fetch historique pays
      fetchCountryHistory();
    }
  }, [user?.id]);

  const fetchKYCData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return;
      }

      const response = await usersService.getUserWithKYC(user.id, token);
      if (response.data && response.data.kyc) {
        setKycData(response.data.kyc);
      }
    } catch (error) {
      console.error("Error fetching KYC data:", error);
    } finally {
      setLoading(false);
    }
  };

  // NOUVELLE FONCTION
  const fetchCountryHistory = async () => {
    if (!user) return;

    setLoadingHistory(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      setCountryHistory([]);
    } catch (error) {
      console.error("Error fetching country history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // NOUVELLE FONCTION
  const handleCountryChange = async (newCountryId: number) => {
    if (!user) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      await usersService.changeUserCountry(user.id, newCountryId, token);

      await fetchCountryHistory();
      setShowCountryModal(false);
      alert("Pays modifié avec succès");
    } catch (error) {
      console.error("Error changing country:", error);
      alert("Erreur lors du changement de pays");
    } finally {
      setActionLoading(false);
    }
  };

  // NOUVELLE FONCTION : Mise à jour de l'utilisateur
  const handleUpdateUser = async (updatedData: Partial<User>) => {
    if (!user) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alertError("Votre session est expirée. Veillez vous reconnecter");
        return;
      }

      const now = new Date().toISOString();

      const data = await usersService.updateUser(
        user.id,
        {
          ...(updatedData as any),
          interact_email: updatedData.interact_email ?? "",
          birthdate: updatedData.birthdate,
          expired_document_date: updatedData.expired_document_date,
          created_at: user.created_at || now,
          updated_at: now,
        },
        token
      );

      if (!data?.data || (data.statusCode && data.statusCode !== 200)) {
        alertError(data?.message || "Erreur lors de la mise à jour de l'utilisateur");
        return;
      }

      const apiUser = data.data.user ?? data.data;
      const mergedUser: User = {
        ...user,
        ...updatedData,
        first_name: apiUser.first_name ?? updatedData.first_name ?? user.first_name,
        last_name: apiUser.last_name ?? updatedData.last_name ?? user.last_name,
        email: apiUser.email ?? updatedData.email ?? user.email,
        phonenumber: apiUser.phonenumber ?? updatedData.phonenumber ?? user.phonenumber,
        interact_email: apiUser.interact_email ?? updatedData.interact_email ?? user.interact_email,
        birthdate: apiUser.birthdate ?? updatedData.birthdate ?? user.birthdate,
        expired_document_date:
          apiUser.expired_document_date ?? updatedData.expired_document_date ?? user.expired_document_date,
        status: (apiUser.status ?? updatedData.status ?? user.status) as User["status"],
        role: (apiUser.role ?? updatedData.role ?? user.role) as User["role"],
        connexion_type: (apiUser.connexion_type ??
          updatedData.connexion_type ??
          user.connexion_type) as User["connexion_type"],
        is_verified: (apiUser.is_verified ?? updatedData.is_verified ?? user.is_verified) as User["is_verified"],
        name: `${apiUser.first_name ?? updatedData.first_name ?? user.first_name} ${
          apiUser.last_name ?? updatedData.last_name ?? user.last_name
        }`.trim(),
        updated_at: apiUser.updated_at ?? now,
      };

      alertSuccess("Utilisateur mis à jour avec succès");
      setShowUpdateUserModal(false);
      onUserUpdate?.(mergedUser);
    } catch (error) {
      console.error("Error updating user:", error);
      alertError("Erreur lors de la mise à jour de l'utilisateur");
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveKYC = async () => {
    if (!user) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      await usersService.approveUserKYC(user.id, token);
      onApprove(user);
    } catch (error) {
      console.error("Error approving KYC:", error);
      alert("Erreur lors de l'approbation du KYC");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectKYC = async (reason: string, notificationMethods: string[]) => {
    if (!user) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await usersService.rejectUserKYCWithReason(user.id, reason, notificationMethods, token);

      const notifications = response.notifications_sent;
      let successMessage = "KYC rejeté avec succès.";
      if (notifications.email && notifications.push) {
        successMessage += " Email et push notification envoyés.";
      } else if (notifications.email) {
        successMessage += " Email envoyé.";
      } else if (notifications.push) {
        successMessage += " Push notification envoyée.";
      }

      alert(successMessage);
      setShowRejectModal(false);
      onReject(user);
    } catch (error) {
      console.error("Error rejecting KYC:", error);
      alert("Erreur lors du rejet du KYC");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;

    const newStatus = user.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const confirmMessage =
      newStatus === "ACTIVE"
        ? "Êtes-vous sûr de vouloir activer ce compte utilisateur ?"
        : "Êtes-vous sûr de vouloir désactiver ce compte utilisateur ?";

    if (!confirm(confirmMessage)) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      await usersService.updateUserStatus(user.id, newStatus, token);
      alert(`Compte ${newStatus === "ACTIVE" ? "activé" : "désactivé"} avec succès`);
      onApprove(user);
    } catch (error) {
      console.error("Error updating user status:", error);
      alert("Erreur lors de la mise à jour du statut");
    } finally {
      setActionLoading(false);
    }
  };

  if (!user) return null;

  const kycDocuments = [
    { label: "Passeport", url: kycData?.passport_image, key: "passport" },
    { label: "CNI/Permis (Recto)", url: kycData?.cni_permit_front_image, key: "cni_front" },
    { label: "CNI/Permis (Verso)", url: kycData?.cni_permit_back_image, key: "cni_back" },
    { label: "Selfie", url: kycData?.selfie_image, key: "selfie" },
  ];

  return (
    <>
      <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">Détails Utilisateur & KYC</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* User Info Header */}
            <div className="flex items-center justify-between pb-4 border-b">
              <div className="flex items-center space-x-4">
                <UserAvatar name={user.name} avatar={user.avatar} />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800">{user.name}</h3>
                  <p className="flex gap-2 items-center text-gray-600">
                    <Mail className="w-4 h-4" />
                    {user.email}
                  </p>
                  {/* MODIFICATION : Ajout du bouton à côté du pays */}
                  <div className="flex gap-4 items-center">
                    <p className="flex gap-2 items-center text-sm text-gray-500">
                      <MapPin className="w-4 h-4" />
                      {user.countryFlag} {user.country}
                    </p>
                    <button
                      onClick={() => setShowCountryModal(true)}
                      className="flex gap-1 items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                    >
                      <Globe className="w-3 h-3" />
                      Modifier le pays
                    </button>
                  </div>
                </div>
              </div>

              {/* NOUVEAU BOUTON : Modifier l'utilisateur */}
              <button
                onClick={() => {
                  navigate("/izilife-pay/update-user", { state: { user } });
                }}
                className="flex gap-2 items-center px-4 py-2 text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700"
              >
                <Edit className="w-4 h-4" />
                Modifier l'utilisateur
              </button>
            </div>

            {/* Detailed User Information */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="flex gap-2 items-center mb-3 font-medium text-gray-800">
                <UserIcon className="w-5 h-5" />
                Informations Personnelles
              </h4>
              <div className="grid grid-cols-2 gap-4">
                {/***
                    <div>
                  <label className="block mb-1 text-xs text-gray-500">ID Utilisateur</label>
                  <p className="px-2 py-1 font-mono text-sm text-gray-800 bg-white rounded">{user.id}</p>
                </div>
                   */}
                <div>
                  <label className="block mb-1 text-xs text-gray-500">Numéro de téléphone</label>
                  <p className="flex gap-2 items-center text-sm text-gray-800">
                    <Phone className="w-4 h-4" />
                    {user.phonenumber || "Non renseigné"}
                  </p>
                </div>
                <div>
                  <label className="block mb-1 text-xs text-gray-500">Email Interac</label>
                  <p className="flex gap-2 items-center text-sm text-gray-800">
                    <Mail className="w-4 h-4" />
                    {user.interact_email || "Non renseigné"}
                  </p>
                </div>
                <div>
                  <label className="block mb-1 text-xs text-gray-500">Type de connexion</label>
                  <p className="flex gap-2 items-center text-sm text-gray-800">
                    <Key className="w-4 h-4" />
                    {user.connexion_type || "EMAIL"}
                  </p>
                </div>
                <div>
                  <label className="block mb-1 text-xs text-gray-500">Rôle</label>
                  <p className="flex gap-2 items-center text-sm text-gray-800">
                    <Shield className="w-4 h-4" />
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        user.role === "ADMIN"
                          ? "bg-purple-100 text-purple-700"
                          : user.role === "MODERATOR"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {user.role || "USER"}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="block mb-1 text-xs text-gray-500">Date d'inscription</label>
                  <p className="flex gap-2 items-center text-sm text-gray-800">
                    <Calendar className="w-4 h-4" />
                    {user.created_at || user.registrationDate}
                  </p>
                </div>
                <div>
                  <label className="block mb-1 text-xs text-gray-500">Dernière activité</label>
                  <p className="flex gap-2 items-center text-sm text-gray-800">
                    <Calendar className="w-4 h-4" />
                    {user.lastActivity}
                  </p>
                </div>
                {/* NOUVEAUX CHAMPS : birthday et expire_date */}
                <div>
                  <label className="block mb-1 text-xs text-gray-500">Date de naissance</label>
                  <p className="flex gap-2 items-center text-sm text-gray-800">
                    <Calendar className="w-4 h-4" />
                    {user.birthdate ? new Date(user.birthdate).toLocaleDateString("fr-FR") : "Non renseignée"}
                  </p>
                </div>
                <div>
                  <label className="block mb-1 text-xs text-gray-500">Date d'expiration</label>
                  <p className="flex gap-2 items-center text-sm text-gray-800">
                    <Calendar className="w-4 h-4" />
                    {user.expired_document_date
                      ? new Date(user.expired_document_date).toLocaleDateString("fr-FR")
                      : "Non renseignée"}
                  </p>
                </div>
              </div>
            </div>

            {/* Informations Complémentaires */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="flex gap-2 items-center mb-3 font-medium text-gray-800">
                <Shield className="w-5 h-5" />
                Informations Complémentaires
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 text-xs text-gray-500">Compte supprimé</label>
                  <p className="text-sm text-gray-800">{user.is_deleted ? "Oui" : "Non"}</p>
                </div>

                <div>
                  <label className="block mb-1 text-xs text-gray-500">Mis à jour le</label>
                  <p className="flex gap-2 items-center text-sm text-gray-800">
                    <Calendar className="w-4 h-4" />
                    {user.updated_at ?? "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* NOUVELLE SECTION : Historique des changements de pays */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="flex gap-2 items-center mb-3 font-medium text-gray-800">
                <History className="w-5 h-5" />
                Historique des changements de pays
              </h4>
              <CountryHistory userId={user.id} />
            </div>

            <div className="space-y-2">
              <div className="text-sm text-gray-500">Transactions totales</div>
              <div className="text-2xl font-semibold">
                {user.transaction_stats?.transaction_count || 0} transactions
              </div>
              {user.transaction_stats && (
                <>
                  <div className="text-sm text-gray-600">
                    Volume envoyé: {user.transaction_stats.total_sent.toLocaleString()}{" "}
                    {user.transaction_stats.total_sent_currency || "CAD"}
                  </div>
                  {user.transaction_stats.total_sent_cad && (
                    <div className="text-xs text-gray-500">
                      (~{user.transaction_stats.total_sent_cad.toLocaleString()} CAD)
                    </div>
                  )}
                </>
              )}
              <button
                className="bg-primary text-white px-4 py-2 rounded hover:opacity-90 transition w-full"
                onClick={() =>
                  navigate(`/izilife-pay/user-transactions/${user.id}`, {
                    state: user,
                  })
                }
              >
                Voir les transactions de <span className="font-semibold">{user.name}</span>
              </button>
            </div>
            {/* Status & Risk */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block mb-1 text-sm text-gray-600">Statut KYC</label>
                <StatusBadge status={user.kycStatus} />
              </div>
              <div>
                <label className="block mb-1 text-sm text-gray-600">is_verified</label>
                {(() => {
                  const badge = getIsVerifiedBadge(user.is_verified);
                  return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                      {badge.label}
                    </span>
                  );
                })()}
              </div>
              <div>
                <label className="block mb-1 text-sm text-gray-600">Statut du Compte</label>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {user.status === "ACTIVE" ? "Actif" : "Inactif"}
                </span>
              </div>
            </div>

            {/* KYC Documents */}
            <div>
              <h4 className="flex gap-2 items-center mb-3 font-medium text-gray-800">
                <FileText className="w-5 h-5" />
                Documents KYC
              </h4>

              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="w-6 h-6 text-green-600 animate-spin" />
                  <span className="ml-2 text-gray-600">Chargement des documents...</span>
                </div>
              ) : kycData ? (
                <div className="grid grid-cols-2 gap-4">
                  {kycDocuments.map((doc) => (
                    <div key={doc.key} className="overflow-hidden rounded-lg border">
                      <div className="px-3 py-2 bg-gray-50 border-b">
                        <p className="text-sm font-medium text-gray-700">{doc.label}</p>
                      </div>
                      <div className="p-3">
                        {doc.url ? (
                          <div
                            className="transition-opacity cursor-pointer hover:opacity-80"
                            onClick={() => setSelectedImage(doc.url ?? null)}
                          >
                            <img src={doc.url} alt={doc.label} className="object-cover w-full h-40 rounded" />
                            <p className="mt-2 text-xs text-center text-green-600">Cliquer pour agrandir</p>
                          </div>
                        ) : (
                          <div className="flex justify-center items-center w-full h-40 bg-gray-100 rounded">
                            <p className="text-sm text-gray-400">Non fourni</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center bg-gray-50 rounded-lg">
                  <p className="text-gray-500">Aucun document KYC trouvé</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="pt-4 space-y-3 border-t">
              {/* KYC Actions */}
              {(user.kycStatus === "pending" ||
                user.kycStatus === "not_requested" ||
                user.kycStatus === "incomplete") &&
                kycData && (
                  <div className="space-y-2">
                    {user.kycStatus !== "pending" && (
                      <div className="p-3 mb-2 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800">
                          <strong>Note:</strong> Ce KYC n'est pas encore en attente de validation. Vous pouvez l'activer
                          ou le rejeter manuellement.
                        </p>
                      </div>
                    )}
                    <div className="flex space-x-3">
                      <button
                        onClick={handleApproveKYC}
                        disabled={actionLoading}
                        className="flex flex-1 gap-2 justify-center items-center px-4 py-2 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Approuver KYC
                      </button>
                      <button
                        onClick={() => setShowRejectModal(true)}
                        disabled={actionLoading}
                        className="flex flex-1 gap-2 justify-center items-center px-4 py-2 text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Rejeter KYC
                      </button>
                    </div>
                  </div>
                )}

              {/* Account Status Toggle */}
              <button
                onClick={handleToggleStatus}
                disabled={actionLoading}
                className={`w-full py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                  user.status === "ACTIVE"
                    ? "bg-orange-600 text-white hover:bg-orange-700"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : user.status === "ACTIVE" ? (
                  <PowerOff className="w-4 h-4" />
                ) : (
                  <Power className="w-4 h-4" />
                )}
                {user.status === "ACTIVE" ? "Désactiver le Compte" : "Activer le Compte"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] p-4">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 right-2 p-2 text-white bg-black bg-opacity-50 rounded-full hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
            <img src={selectedImage} alt="Document preview" className="max-w-full max-h-[85vh] object-contain" />
          </div>
        </div>
      )}

      {/* Reject KYC Modal */}
      <RejectKYCModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={handleRejectKYC}
        userName={user.name}
        loading={actionLoading}
      />

      {/* NOUVEAU : Change Country Modal */}
      <ChangeCountryModal
        isOpen={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        onConfirm={handleCountryChange}
        currentCountry={user.country}
        countries={countries}
        loading={actionLoading}
      />

      {/* NOUVEAU : Update User Modal */}
      <UpdateUserModal
        isOpen={showUpdateUserModal}
        onClose={() => setShowUpdateUserModal(false)}
        onConfirm={handleUpdateUser}
        user={user}
        loading={actionLoading}
      />
    </>
  );
};
